import { motion } from "framer-motion";
import { Calendar, MapPin, Users } from "lucide-react";
import { useState, useEffect } from "react";
import heroImage from "../../assets/hero-beach.jpg";

const TripHero = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    // Set this to whatever future date/time you want the countdown to target
    const targetDate = new Date("2026-05-17T00:00:00").getTime();

    const updateCountdown = () => {
      const now = new Date().getTime();
      const difference = targetDate - now;

      if (difference <= 0) {
        setTimeLeft({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
        });
        return;
      }

      setTimeLeft({
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000),
      });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  const countdownItems = [
    { label: "Days", value: timeLeft.days },
    { label: "Hours", value: timeLeft.hours },
    { label: "Minutes", value: timeLeft.minutes },
    { label: "Seconds", value: timeLeft.seconds },
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={heroImage}
          alt="Portugal destination"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground/40 via-foreground/20 to-background" />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-4 max-w-4xl mx-auto pt-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <span className="inline-block px-4 py-2 rounded-full bg-card/20 backdrop-blur-md text-primary-foreground text-sm font-medium mb-6">
            Spring 2026 Adventure
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          className="text-5xl md:text-7xl lg:text-8xl font-serif font-bold text-primary-foreground mb-6 leading-tight"
        >
          Portugal
          <span className="block text-3xl md:text-4xl lg:text-5xl font-light mt-2">
            Adventure Awaits
          </span>
        </motion.h1>

        {/* Countdown */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
          className="flex justify-center gap-4 md:gap-6 mt-8 mb-10"
        >
          {countdownItems.map((item, index) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 + index * 0.1 }}
              className="flex flex-col items-center"
            >
              <div className="glass-card rounded-xl px-4 py-3 md:px-6 md:py-4 min-w-[60px] md:min-w-[80px]">
                <motion.span
                  key={item.value}
                  initial={{ scale: 1.2 }}
                  animate={{ scale: 1 }}
                  className="text-2xl md:text-4xl font-bold text-foreground block"
                >
                  {String(item.value).padStart(2, "0")}
                </motion.span>
              </div>
              <span className="text-xs md:text-sm text-primary-foreground/80 mt-2 uppercase tracking-wider">
                {item.label}
              </span>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}
          className="flex flex-wrap justify-center gap-6 mt-6"
        >
          <div className="flex items-center gap-2 px-5 py-3 rounded-full glass-card text-foreground">
            <Calendar className="w-5 h-5 text-primary" />
            <span className="font-medium">Sun May 17 – Wed May 27, 2026</span>
          </div>
          <div className="flex items-center gap-2 px-5 py-3 rounded-full glass-card text-foreground">
            <MapPin className="w-5 h-5 text-primary" />
            <span className="font-medium">Lisbon, Portugal</span>
          </div>
          <div className="flex items-center gap-2 px-5 py-3 rounded-full glass-card text-foreground">
            <Users className="w-5 h-5 text-primary" />
            <span className="font-medium">6 Travelers</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.8 }}
          className="mt-16"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="text-primary-foreground/80"
          >
            <span className="text-sm tracking-widest uppercase">Scroll to explore</span>
            <div className="w-px h-12 bg-primary-foreground/40 mx-auto mt-4" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default TripHero;